import {useEffect} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {deriveRevalidatedStatuses} from '../helpers/unified-batch-revalidation'
import {useAllThreads, useUnifiedBatch, useUnifiedBatchActions} from '../stores/layout-selectors'

/**
 * Keeps every batched item's `isOutdated` in sync with the live review threads, replacing the old
 * batch-wide "one push marks everything outdated" cascade with per-item revalidation.
 *
 * It reconciles reactively: the effect only does work when the threads actually change — a Refresh
 * soft-nav reseeding the diff-summary markers, positioning settling in later, or old-head restore
 * hydrating recovered items. `revalidateItems` returns the same state reference when nothing changed,
 * so this converges (and no-ops) rather than looping.
 *
 * Fail-safe by construction: `deriveRevalidatedStatuses` omits items whose thread/comment isn't
 * present (keeping their current status) and never removes items — outdated items stay in the batch
 * as Fix-with-Copilot context (issue #902).
 *
 * This never advances `revalidatedHeadSha`: it calls `revalidateItems` without a head, so it only
 * refines per-item status. Refresh owns the head baseline (see `RefreshButton`), advancing it from
 * data it fetched at the new head.
 *
 * Gated on `unified_batch_pr_comments`; a no-op when the flag is off (and `revalidateItems` carries
 * its own flag gate as a belt-and-suspenders guard).
 *
 * Mount this once under the layout store on the PR page (it renders no UI).
 */
export function useRevalidateBatchOutdated(): void {
  const enabled = isFeatureEnabled('unified_batch_pr_comments')
  const threads = useAllThreads()
  const {items} = useUnifiedBatch()
  const {revalidateItems} = useUnifiedBatchActions()

  useEffect(() => {
    if (!enabled) return
    revalidateItems(deriveRevalidatedStatuses(items, threads))
  }, [enabled, items, threads, revalidateItems])
}
